import { useEffect, useMemo, useState } from 'react';
import {
  fetchPositionsForWA,
  previewContract,
  createContract,
  previewContractBundle,
  Position,
  PreviewResponse,
  ContractCreationPayload,
  SchemaField,
  BlockSchemaField,
  AttachmentPayload,
} from '../services/ContractWizardService';
import {
  listTemplates,
  getTemplate,
  Template,
  seedTemplates,
  previewTemplate,
  getAnnexTemplates,
  TemplateSummary,
} from '../../templates/services/TemplatesService';
import {
  fetchAttachments,
  AttachmentTemplate,
  AttachmentSelection,
} from '../services/AttachmentService';
import { useCompensationUnlock } from '../../employees/hooks/useCompensationUnlock';
import { EMPLOYMENT_TYPE_OPTIONS } from '../../employees/utils/constants';
import { useUserRole } from '../../../hooks/useUserRole';
import { toast } from 'react-toastify';
import { absolutizePdfUrl } from '../../../utils/pdfUtils';
import {
  derivePresenceFromSchema,
  validateConditionals,
  serializeConditionalValues,
  getFallbackPresence,
  ConditionalFieldPresence,
} from '../utils/conditionals';

/* ================================================
   Constants
   ================================================ */

export const ANY_POSITION = {
  position_key: null,
  label: 'Allgemein (ohne Position)',
  _virtual: true,
};

export const STEP1_KEYS = new Set<string>([
  'employment_type',
  'start_date',
  'work_area_id',
  'position_key',
]);

export const COMPENSATION_KEYS = new Set<string>([
  'hourly_rate',
  'monthly_hours',
  'contract_hours_per_week',
  'salary_monthly_cents',
]);

/* ================================================
   Pure utility helpers
   ================================================ */

export const toUppercaseCompType = (
  type: 'hourly' | 'salary'
): 'HOURLY' | 'SALARY' => {
  return type === 'hourly' ? 'HOURLY' : 'SALARY';
};

export const weeklyToMonthly = (weekly: number): number => {
  return Math.round(((weekly * 52) / 12) * 100) / 100;
};

export const monthlyToWeekly = (monthly: number): number => {
  return Math.round(((monthly * 12) / 52) * 100) / 100;
};

export const norm = (s?: string) => (s || '').trim().toUpperCase();

export const calculateProbationEndDate = (
  start: string,
  months: number
): string => {
  const d = new Date(start);
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
};

/**
 * Helper to find a template by ID or KEY.
 * Handles both numeric IDs (e.g., "32") and template keys
 * (e.g., "CONTRACT_SERVICE_MINIJOB_DE_V2").
 */
export const findTemplate = (
  templates: Template[],
  idOrKey: string
): Template | undefined => {
  const templateIdNum = parseInt(idOrKey);
  const isNumericId =
    !isNaN(templateIdNum) && idOrKey === String(templateIdNum);

  return isNumericId
    ? templates.find((t) => t.id === templateIdNum)
    : templates.find((t) => t.template_key === idOrKey);
};

/**
 * Determine if a block can be edited based on the user's role.
 */
export const canEditBlock = (
  editableBy: 'admin' | 'manager' | 'all',
  isAdmin: boolean,
  isManager: boolean
): boolean => {
  if (editableBy === 'all') return true;
  if (editableBy === 'manager') return isAdmin || isManager;
  if (editableBy === 'admin') return isAdmin;
  return false;
};

/* ================================================
   Props interface (shared across sub-components)
   ================================================ */

export interface ContractWizardModalProps {
  open: boolean;
  onClose: () => void;
  employeeId: number;
  workAreas: Array<{ id: number; name: string }>;
  currentEmploymentType?: string;
  currentWorkAreaId?: number;
  currentPositionKey?: string;
  currentPositionTitle?: string;
  currentStartDate?: string;
  onSuccess?: (data: {
    document_id: string;
    pdf_url: string;
    trace_id?: string;
    merge?: { success: boolean; warnings?: string[] };
  }) => void;
  asDrawer?: boolean;
}

/* ================================================
   Validation helpers
   ================================================ */

export function validateRequiredFields(
  preview: PreviewResponse | null,
  selectedEmploymentType: string,
  startDate: string,
  compensationType: 'hourly' | 'salary',
  formValues: Record<string, any>,
  presence: ConditionalFieldPresence
): Record<string, string> {
  if (!preview?.schema) return {};

  const errors: Record<string, string> = {};

  if (!selectedEmploymentType) {
    errors['employment_type'] = 'Beschäftigungsart fehlt';
  }
  if (!startDate) {
    errors['start_date'] = 'Beginn fehlt';
  }

  if (compensationType === 'hourly') {
    if (!formValues['hourly_rate'] || formValues['hourly_rate'] <= 0) {
      errors['hourly_rate'] = 'Stundenlohn fehlt';
    }
    if (
      !formValues['contract_hours_per_week'] ||
      formValues['contract_hours_per_week'] <= 0
    ) {
      errors['contract_hours_per_week'] = 'Wochenstunden fehlen';
    }
  } else {
    if (
      !formValues['salary_monthly_cents'] ||
      formValues['salary_monthly_cents'] <= 0
    ) {
      errors['salary_monthly_cents'] = 'Monatsgehalt (Cent) fehlt';
    }
    if (!formValues['monthly_hours'] || formValues['monthly_hours'] <= 0) {
      errors['monthly_hours'] = 'Monatsstunden fehlen';
    }
  }

  const requiredFields = Array.isArray(preview.schema.required_fields)
    ? preview.schema.required_fields
    : [];
  requiredFields.forEach((field) => {
    if (!field || !field.key) return;
    if (STEP1_KEYS.has(field.key) || COMPENSATION_KEYS.has(field.key)) return;
    if (field.step_hint === 'basic_info') return;

    const value = formValues[field.key];
    if (value === undefined || value === null || value === '') {
      errors[field.key] = `${field.label || field.key} is required`;
    }
  });

  const conditionalErrors = validateConditionals(formValues, presence);
  Object.assign(errors, conditionalErrors);

  return errors;
}

export function validateAttachmentFields(
  attachmentSelections: AttachmentSelection[],
  attachments: AttachmentTemplate[]
): Record<string, string> {
  const errors: Record<string, string> = {};

  attachmentSelections.forEach((selection) => {
    const attachment = attachments.find(
      (a) => a.template_id === selection.template_id
    );
    if (!attachment) return;

    attachment.variables_schema?.forEach((variable) => {
      if (variable.required) {
        const value = selection.variables?.[variable.key];
        if (!value || value === '') {
          const errorKey = `${attachment.template_id}_${variable.key}`;
          errors[errorKey] = `${variable.label} ist erforderlich`;
        }
      }
    });
  });

  return errors;
}

/**
 * Check if the form is valid (for enabling the Create button).
 */
export function isFormComplete(
  preview: PreviewResponse | null,
  selectedEmploymentType: string,
  startDate: string,
  compensationType: 'hourly' | 'salary',
  formValues: Record<string, any>
): boolean {
  if (!preview?.schema) return true;

  if (!selectedEmploymentType || !startDate) return false;

  if (compensationType === 'hourly') {
    if (!formValues['hourly_rate'] || formValues['hourly_rate'] <= 0)
      return false;
    if (
      !formValues['contract_hours_per_week'] ||
      formValues['contract_hours_per_week'] <= 0
    )
      return false;
  } else {
    if (
      !formValues['salary_monthly_cents'] ||
      formValues['salary_monthly_cents'] <= 0
    )
      return false;
    if (!formValues['monthly_hours'] || formValues['monthly_hours'] <= 0)
      return false;
  }

  const requiredFields = preview.schema.required_fields || [];
  return requiredFields.every((field) => {
    if (STEP1_KEYS.has(field.key) || COMPENSATION_KEYS.has(field.key))
      return true;
    if (field.step_hint === 'basic_info') return true;

    const value = formValues[field.key];
    return value !== undefined && value !== null && value !== '';
  });
}

/* ================================================
   Filtered fields helpers
   ================================================ */

const CONDITIONAL_KEYS = new Set([
  'is_fixed_term',
  'contract_end_date',
  'fixed_term_reason',
  'has_other_employment',
  'other_employment_employer',
  'other_employment_street',
  'other_employment_house_no',
  'other_employment_postal_code',
  'other_employment_city',
  'other_employment_start_date',
  'other_employment_monthly_salary',
]);

export function filterRequiredFields(
  fields: SchemaField[] | undefined
): SchemaField[] {
  if (!fields) return [];
  return fields.filter((f) => {
    if (STEP1_KEYS.has(f.key)) return false;
    if (COMPENSATION_KEYS.has(f.key)) return false;
    if (CONDITIONAL_KEYS.has(f.key)) return false;
    if (f.step_hint === 'basic_info') return false;
    return true;
  });
}

export function filterOptionalFields(
  fields: SchemaField[] | undefined,
  selectedPositionKey: string | null
): SchemaField[] {
  if (!fields) return [];
  return fields.filter((f) => {
    if (STEP1_KEYS.has(f.key)) return false;
    if (COMPENSATION_KEYS.has(f.key)) return false;
    if (CONDITIONAL_KEYS.has(f.key)) return false;
    if (f.step_hint === 'basic_info') return false;
    if (f.key === 'employee.position_title') {
      return selectedPositionKey === null;
    }
    return true;
  });
}

/* ================================================
   Build overrides / payload helpers
   ================================================ */

export function buildVariables(
  employeeId: number,
  formValues: Record<string, any>,
  presence: ConditionalFieldPresence,
  startDate: string,
  selectedEmploymentType: string,
  compensationType: 'hourly' | 'salary',
  selectedPositionKey: string | null,
  positions: Position[],
  employeePositionTitle?: string,
  roleDescriptionText?: string
): Record<string, any> {
  const normalizedEmploymentType = norm(selectedEmploymentType);
  const conditionalOverrides = serializeConditionalValues(
    formValues,
    presence
  );

  const variables: Record<string, any> = {
    employee_id: employeeId,
    ...formValues,
    ...conditionalOverrides,
    start_date: startDate,
    employment_type: normalizedEmploymentType,
    'employee.position_title': employeePositionTitle,
    ...(roleDescriptionText
      ? { role_description_text: roleDescriptionText }
      : {}),
  };

  if (compensationType === 'hourly') {
    variables['hourly_rate'] = formValues['hourly_rate'];
    variables['monthly_hours'] = formValues['monthly_hours'];
  } else {
    variables['salary_monthly_cents'] = formValues['salary_monthly_cents'];
    variables['monthly_hours'] = formValues['monthly_hours'];
  }

  return variables;
}

export function resolvePositionTitles(
  selectedPositionKey: string | null,
  positions: Position[],
  formValues: Record<string, any>
): {
  employeePositionTitle: string;
  roleDescriptionText: string | undefined;
  positionTitle: string | undefined;
} {
  const isAnyPosition = selectedPositionKey === null;

  if (isAnyPosition) {
    return {
      employeePositionTitle:
        formValues['employee.position_title'] || 'Mitarbeiter',
      roleDescriptionText: formValues['role_description_text'],
      positionTitle:
        formValues['employee.position_title'] || 'Mitarbeiter',
    };
  }

  const selectedPosition = positions.find(
    (p) => p.position_key === selectedPositionKey
  );
  return {
    employeePositionTitle:
      formValues['employee.position_title'] ||
      selectedPosition?.label ||
      'Mitarbeiter',
    roleDescriptionText:
      formValues['role_description_text'] ||
      selectedPosition?.duties_richtext_default,
    positionTitle: selectedPosition?.label || undefined,
  };
}

export type { PreviewResponse, SchemaField, BlockSchemaField, Position };
export type { Template, AttachmentTemplate, AttachmentSelection };
export type { ConditionalFieldPresence };
